import React from 'react';
import contensis from 'Common/api/contensis';
import { Op, OrderBy } from 'contensis-delivery-api';
import { excludeMetadata } from 'Common/util/contensis/api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, PageData } from 'Templates/Accommodation/FindAHome/Listing';

const AccommodationListing: NextPageWithLayout<PageData> = ({ entry, ancestors, residences }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    ancestors,
                    residences
                })}
            />
        </>
    );
};

AccommodationListing.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: '/accommodation/find-a-home',
            entryLinkDepth: 2,
            entryFields: ['*', ...excludeMetadata]
        });

        if (!resPage.entry) {
            return {
                error: true
            };
        }

        const residenceQuery = Op.equalTo('sys.contentTypeId', 'studentResidenceInformation');

        const resResidences = await search(residenceQuery, {
            orderBy: OrderBy.asc('title'),
            pageSize: 10,
            linkDepth: 1,
            fields: [
                'listingImage',
                'listingImageLandscape',
                'title',
                'heroBannerImageDefault.heroBannerWaveDefault.postHeading',
                'distanceFromCampus',
                'introText',
                'priceFrom',
                'residenceFeatures',
                'listItemText',
                'icon'
            ]
        });

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        return {
            entry: resPage.entry,
            ancestors,
            residences: resResidences.items.filter(item => item.sys.uri)
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default AccommodationListing;
